import {
    BaseChart,
    ChartMouseHandler,
    EChartsOption,
    getConfiguredInstance,
    useChart,
} from '@/components/ECharts';
import { PeriodOption } from '@/config/model';
import { NumberFormatter, useConfigContext } from '@/context/configContext';
import { useSpendSummaryContext } from '@/dashboards/SpendSummary/hooks';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { fillDateGaps } from '@/utils/fillDateGaps';
import { jsxToHtml } from '@/utils/reactUtils';
import { transformWeeklyToMonthlySeries } from '@/utils/weeklyToMonthlyUtils';
import { Typography } from '@analytical-alley/ui';
import { BarSeriesOption } from 'echarts';
import EChartsReact from 'echarts-for-react';
import { isArray } from 'lodash';
import React, {
    Ref,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

interface GraphProps {
    title: string;
    isInversed?: boolean;
    chartRef?: Ref<EChartsReact | null>;
    selected: string;
    onSelected: (value: string) => void;
    onSeriesData?: (chartOptionSeries: ChartOptions['series']) => void;
}

type AreaTooltipProps = {
    seriesName: string;
    color: string;
    value: number;
    axisValueLabel: string;
}[];

type ChartOptions = EChartsOption<BarSeriesOption>;

const AreaTooltip = ({
    modelBreakdown,
    params,
    selectedSeriesName,
    graphTitle,
    formatNumber,
}: {
    modelBreakdown: 'category' | 'variable';
    params: AreaTooltipProps;
    selectedSeriesName?: string;
    graphTitle: string;
    formatNumber: NumberFormatter;
}) => {
    if (!selectedSeriesName) {
        return null;
    }

    const hoveredItem = params.find(
        (param) => param.seriesName === selectedSeriesName,
    );

    if (!hoveredItem) {
        return null;
    }

    const totalValue = params.reduce((sum, param) => sum + param.value, 0);

    return (
        <div className="tooltip-content">
            <table>
                <tbody>
                    <tr>
                        <td className="pr-2">
                            <Typography className="text-black dark:text-black">
                                {modelBreakdown === 'category'
                                    ? 'Category'
                                    : 'Variable'}
                            </Typography>
                        </td>
                        <td>
                            <Typography className="text-dark dark:text-dark">
                                {hoveredItem.seriesName}
                            </Typography>
                        </td>
                    </tr>
                    <tr>
                        <td className="pr-2">
                            <Typography className="text-black dark:text-black">
                                Chosen period:
                            </Typography>
                        </td>
                        <td>
                            <Typography className="text-dark dark:text-dark">
                                {params[0]?.axisValueLabel}
                            </Typography>
                        </td>
                    </tr>
                    <tr>
                        <td className="pr-2">
                            <Typography className="text-black dark:text-black">
                                {`${graphTitle} Share:`}
                            </Typography>
                        </td>
                        <td>
                            <Typography className="text-dark dark:text-dark">
                                {`${totalValue > 0 ? Math.round((hoveredItem.value / totalValue) * 100) : hoveredItem.value}%`}
                            </Typography>
                        </td>
                    </tr>
                    <tr>
                        <td className="pr-2">
                            <Typography className="text-black dark:text-black">
                                {`${graphTitle}:`}
                            </Typography>
                        </td>
                        <td>
                            <Typography className="text-dark dark:text-dark">
                                {formatNumber(hoveredItem.value)}
                            </Typography>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const getGraphData = ({
    isInversed,
    seriesData,
    xAxisData,
    period,
    formatNumber,
}: {
    isInversed: boolean;
    xAxisData: string[];
    seriesData: {
        [key: string]: {
            data: { [key: string]: number };
            color: string;
        };
    };
    period: string | undefined;
    formatNumber: NumberFormatter;
}): ChartOptions => {
    // Use utility for week-to-month transformation
    const {
        xAxisData: transformedXAxisData,
        seriesData: transformedSeriesData,
    } = transformWeeklyToMonthlySeries(xAxisData, seriesData, period);

    const dates = transformedXAxisData.filter((date, index) => {
        return transformedXAxisData.indexOf(date) === index;
    });
    const datesWithGaps = fillDateGaps(dates, period as PeriodOption);

    const series: ChartOptions['series'] = Object.entries(
        transformedSeriesData,
    ).map(([key, { data, color }]) => {
        return {
            name: key,
            type: 'bar',
            stack: 'Total',
            symbolSize: 2,
            showSymbol: false,
            showAllSymbol: false,
            triggerLineEvent: true,
            color: color,
            barMaxWidth: 60,
            barMinWidth: 2,
            data: datesWithGaps.map((date) => ({
                name: date,
                value: data[date] || 0,
            })),
        } as unknown as ChartOptions['series'][0];
    });

    return {
        tooltip: {
            show: false,
            trigger: 'axis',
            triggerOn: 'mousemove|click',
            backgroundColor: 'transparent',
            borderWidth: 0,
            padding: 10,
            type: 'item',
            confine: true,
            extraCssText: 'max-width: 600px; white-space: pre-wrap;',
            formatter: '',
        },
        legend: {
            show: false,
        },
        grid: {
            right: '1%',
            left: '7%',
            top: isInversed ? '5%' : '15%',
        },
        xAxis: [
            {
                type: 'category',
                boundaryGap: true,
                show: !isInversed,
                axisLine: {
                    show: false,
                },
                data: datesWithGaps,
                axisLabel: {
                    rotate: 90,
                    margin: period === 'yearly' ? 30 : 20,
                },
            },
        ],
        yAxis: [
            {
                type: 'value',
                inverse: isInversed,
                axisLabel: {
                    formatter: (value: number) =>
                        formatNumber(value, {
                            locale: 'en-US',
                            notation: 'compact',
                        }),
                },
                axisLine: {
                    show: false,
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        opacity: 0.1,
                    },
                },
            },
        ],
        series: series,
    };
};

export const MediaSpendVsContributionGraph = memo(
    ({
        title,
        isInversed = false,
        chartRef,
        selected,
        onSelected,
        onSeriesData,
    }: GraphProps) => {
        const { formatNumber: generalFormatter } = useConfigContext();
        const { contributionFormatter } = useDashboardGlobalContext();
        const formatNumber =
            title === 'Spend' ? generalFormatter : contributionFormatter;

        const {
            spendSummaryData,
            getVisibleContributionData,
            getVisibleSpendData,
            modelBreakdown,
            period,
        } = useSpendSummaryContext();

        const eChartsOptions = useMemo(() => {
            return getGraphData({
                isInversed,
                seriesData:
                    title === 'Spend'
                        ? getVisibleSpendData()
                        : getVisibleContributionData(),
                xAxisData: spendSummaryData.dates,
                period: period,
                formatNumber,
            });
        }, [
            formatNumber,
            getVisibleContributionData,
            getVisibleSpendData,
            isInversed,
            period,
            spendSummaryData.dates,
            title,
        ]);

        const [hovered, setHovered] = useState('');

        const selectedRef = useRef(selected);
        const timeoutRef = useRef<number | null>(null);

        useEffect(() => {
            onSeriesData?.(eChartsOptions.series);
        }, [eChartsOptions, onSeriesData]);

        useEffect(() => {
            selectedRef.current = selected;
        }, [selected]);

        const onClick: ChartMouseHandler = useCallback(
            (context) => {
                if (context.seriesName) {
                    onSelected(context.seriesName);
                } else {
                    onSelected('');
                }
            },
            [onSelected],
        );

        const onMouseOver: ChartMouseHandler = useCallback((context) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (context.seriesName) {
                setHovered(
                    selectedRef.current
                        ? selectedRef.current
                        : context.seriesName,
                );
            }
        }, []);

        const onMouseOut: ChartMouseHandler = useCallback(() => {
            if (selectedRef.current) {
                return;
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = window.setTimeout(() => {
                setHovered('');
            }, 0);
        }, []);

        const { ref, chartOptions, onEvents, isChartReady, onChartReady } =
            useChart({
                chartOptions: eChartsOptions,
                onClick,
                onMouseOver,
                onMouseOut,
            });

        useEffect(() => {
            // Guarded: until the option has landed, `BaseChart` renders with
            // `notMerge`, so anything set here would be discarded — and a
            // series-carrying partial option would throw `xAxis "0" not found`.
            const instance = getConfiguredInstance(ref.current);

            if (instance) {
                instance.setOption({
                    tooltip: {
                        showDelay: selected ? 0 : 50,
                        show: true,
                        formatter: (params: AreaTooltipProps) => {
                            return jsxToHtml(
                                <AreaTooltip
                                    modelBreakdown={modelBreakdown}
                                    params={params}
                                    selectedSeriesName={hovered}
                                    graphTitle={title}
                                    formatNumber={formatNumber}
                                />,
                            );
                        },
                    },
                });
            }
        }, [
            ref,
            hovered,
            selected,
            title,
            modelBreakdown,
            formatNumber,
            isChartReady,
        ]);

        useEffect(() => {
            const instance = getConfiguredInstance(ref.current);

            const series =
                chartOptions?.series && isArray(chartOptions.series)
                    ? chartOptions.series
                    : [];

            if (instance) {
                instance.setOption({
                    series: series.map((seriesOption) => {
                        if (selected && selected !== seriesOption.name) {
                            return {
                                ...seriesOption,
                                areaStyle: { opacity: 0.1 },
                                itemStyle: { opacity: 0.1 },
                                lineStyle: { opacity: 0 },
                                symbol: 'none',
                            };
                        }
                        return {
                            ...seriesOption,
                            areaStyle: { opacity: 0.8 },
                            itemStyle: { opacity: 0.8 },
                            lineStyle: { opacity: 0.6 },
                            symbol: 'circle',
                        };
                    }),
                });
            }
        }, [ref, chartOptions.series, selected, isChartReady]);

        useImperativeHandle(chartRef, () => ref.current, [ref]);

        return (
            <div className="relative w-full grid grid-cols-[2rem_1fr]">
                <div className="self-center">
                    <Typography className="-rotate-90" variant="bodyM">
                        {title}
                    </Typography>
                </div>
                <div className="h-90 w-full">
                    <BaseChart
                        $shouldExpand
                        option={chartOptions}
                        ref={ref}
                        onEvents={onEvents}
                        onChartReady={onChartReady}
                    />
                </div>
            </div>
        );
    },
);

MediaSpendVsContributionGraph.displayName = 'MediaSpendVsContributionGraph';
